/**
 * Common Constants
 * this file contains general and basic constants
 *
 * Naming Convention: SCREAMING_SNAKE_CASE
 * i.e. COMMON_CONSTANT
 */

import { LANGUAGE } from '@tajawal/web-core-sdk-v2/lib/constants';
import { INBOUND, OUTBOUND } from '@tajawal/web-flights-sdk/lib/constants';
import { FILTER_MAP } from '@tajawal/web-flights-sdk/lib/constants/filters.constants';

/**
 * Numbers
 */
export const MIN_SLIDER_VALUE = 0;

export const SEARCH_QUERY_MIN_LENGTH = 2;
export const BANNER_OFFSET = 1;
export const MAX_CALLS_FARE_CALENDAR_BULK = 6;
export const CHUNK_DAYS_FARE_CALENDAR_BULK = 60;

export const AUTH_STATE_MAP = {
  LOGGED_IN: 'loggedIn',
  REGISTERED: 'registered',
  LOGGED_IN_CLOSED: 'loggedInClose',
};

/**
 * Links
 */
export const API_BASE_URL = 'https://next-dev.almosafer.com/';
export const DOWNLOAD_APP_LINKS = {
  en: 'https://apps.almosafer.com/69xl/dozyi9cc',
  ar: 'https://apps.almosafer.com/69xl/h483ifnm',
};

export const APP_PAGES = {
  HOME: 'Home',
  LISTING: 'Activities List',
  DETAILS: 'Activity Details',
  GUEST: 'Guest Details',
  EXTRA: 'Extra Info',
  PAYMENT: 'Payment',
} as const;

export const TEST_ID_DELIMITER = '__';
export const BRAND = 'almosafer';
export const FARE_CALENDAR_RANGE = 7;
export const RECENT_SEARCH_RESULTS_LIMIT = 4;
export const defaultLocale = LANGUAGE.EN;

/**
 * Collections
 */
export const CREDENTIAL_KEYS = {
  MWEB: 'P4D8u1bsP14GpOHCOJn7jK1S',
  DESKTOP: 'skdjfh73273$7268u2j89s',
};

export const TEST_ID_PREFIX = {
  SESSION_EXPIRY_MODAL: 'SessionExpiryModal',
  RESERVATION_ERROR_MODAL: 'ReservationErrorModal',
  SERVER_ERROR: 'ServerError',
  EMAIL_LABEL: `FlightPAX__InnerLogin__EmailLabel`,
  SIGN_IN_MODAL_TRIGGER: `FlightPAX__InnerLogin__SignInButton`,
  SEARCH_PAGE: 'FlightSearchResults',
  PAX_PAGE: 'Flights_PaxPage',
  SEARCH_RESULT_CARD: 'SearchResultCard',
  FLIGHTS_DETAILS: 'FlightsDetails',
  NO_FLIGHT_AVAILABLE_MODAL: 'FlightNotAvailableModal',
};

/**
 * miscellaneous
 */

export const AIRLINE_IMAGE_URL = 'https://tjwlcdn.com/img/air';

export const PAX_FORM_ID = 'travellerDetailsForm';
export const PAX_SUB_FORM_ID = 'travellerDetailsSubForm';
export const PAX_SUB_FORM_BUTTON_ID = 'travellerDetailsSubFormButton';

export const APIS = {
  SEAT_RESERVATION: 'Seat reservation API',
  PRICING: 'Pricing API',
  ADDONS: 'Addons API',
};

export const FARE_CALENDAR_TABLE_HEADER_ID = 'FareCalendarTableHeader';
export const FARE_CALENDAR_TABLE_ID = 'FareCalendarTable';
export const FARE_CALENDAR_TEST_ID = 'FlightSearchResult__FareCalendar';
export const SEAT_NOT_FOUND_ERROR = 'The seats you have selected are no longer available, please choose another flight';
export const SPECIAL_FORMAT_DIAL_CODES = ['+971', '+966'];
export const OTHER_FORMAT_DIAL_CODES = ['+962', '+965', '+974', '+973', '+968', '+20'];
export const TRAVELLERS_TYPE_LABELS = { ADT: 'Adult', CH: 'Child', INF: 'Infant' };
export const INPUT_UPDATE_TIMEOUT = 750;
export const CHECKBOX_UPDATE_TIMEOUT = 100;
export const CONTACT_FIELDS = ['title', 'firstName', 'lastName', 'email', 'phone', 'dialCode', 'countryId', 'fullName'];
export const BAGGAGE_FILTER_KEY = 'hasBaggageOnly';
export const BAGGAGE_FILTER_OPTIONS_KEYS = {
  HAS_BAGGAGE_ONLY: 'HAS_BAGGAGE_ONLY',
  ANY_BAGGAGE: 'ANY_BAGGAGE',
};

export const PARTNER_REFERER = 'partner_referer';
export const META = {
  FLIGHTS_HOME: {
    TITLE: {
      en: 'Cheap Flights, Flight Bookings & Airline Tickets Online | Almosafer',
      ar: 'حجز طيران بأفضل أسعار | أرخص رحلات طيران وتذاكر طيران | المسافر',
    },
    DESCRIPTION: {
      en: 'Book cheap flights tickets with Almosafer 450+ Airlines ✓ best prices Guaranteed ✓ fast & easy online booking ✓ 24/7 customer support ✓ pay in installments ✓ no hidden charges',
      ar: 'احجز رحلات طيران رخيصة عبر موقع المسافر! اختر من بين 450+ شركة طيران ✓ أفضل أسعار أونلاين ✓ حجز سهل وسريع ✓ خدمة العملاء 24/7 دفع بالأقساط 0% فائدة ✓ لا توجد رسوم خفية',
    },
  },
};

export const MODALS_HASH = {
  SEARCH_BOX_AIRPORT: 'SearchBoxAirport',
  SEARCH_BOX_CABIN: 'SearchBoxCabin',
  SEARCH_BOX_PASSENGERS: 'SearchBoxPassengers',
  SEARCH_BOX_PASSENGERS_AND_CABIN: 'SearchBoxPassengersAndCabin',
  SAME_AIRPORT_ERROR: 'SameAirportError',
  SEARCH_BOX_DATE: 'SearchBoxDate',
  DATE_PICKER: 'DatePicker',
  BAGGAGE_INFO: 'BaggageInfo',
  FARE_RULES: 'FareRules',
  CANCEL_AND_CHANGE: 'CancelAndChange',
  FAQ_INFO: 'FaqInfo',
  FARE_SUMMARY_INFO: 'FareSummaryInfo',
  REWARDS_CARD: 'RewardsCard',
  PAX_EXTRA_BAGGAGE: 'PaxExtraBaggage',
  PAX_EXTRA_BAGGAGE_SELECT: 'PaxExtrabaggageSelect',
  DIAL_CODE: 'DialCode',
  PAX_FARE_TOTALS: 'PaxFareTotals',
  PAX_BOOKING_SUMMARY: 'PaxBookingSummary',
  STEP_BY_STEP_FARE_FAMILIES: 'StepByStepFareFamilies',
  COUNTRY_SELECT: 'CountrySelect',
  DOCUMENT_INFO: 'DocumentInfo',
  TRAVELLER_FORM: 'TravellerForm',
  FREQUENT_FLYRER_NUMBER: 'FrequentFlyerNumber',
  FREQUENT_FLYRER_SELECT: 'FrequentFlyerSelect',
  MARKETING_CARRIER: 'MarketingCarrier',
  DOCUMENT_ID: 'DocumentID',
  ADDON_REMOVE: 'AddOnRemove',
  ADDON_TNC: 'AddOnTnC',
  ADDON_BENEFITS: 'AddOnBenefits',
  DOWNLOAD_DRAWER: 'DownloadDrawer',
  PAX_SIGN_IN: 'PaxSignIn',
  INSURANCE_DETAILS: 'InsuranceDetails',
  FLIGHT_DETAILS: 'FlightDetails',
  PRICE_BREAKDOWN: 'PriceBreakdown',
  FLIGHT_CONDITIONS: 'FlightConditions',
  SEARCH_RESULT_GROUP: 'SearchResultGroup',
  MARKETING_BANNER_DETAILS: 'MarketingBannerDetails',
  AIRLINES_FILTER: 'AirlinesFilter',
  SINGLE_SELECT_DRAWER: 'SingleSelectDrawer',
  CURRENCY_PICKER: 'CurrencyPicker',
  FARE_FAMILIES: 'FareFamilies',
  MODIFY_SEARCH: 'ModifySearch',
  INSURANCE: 'Insurance',
  SORT_DRAWER: 'SortDrawer',
  FILTERS_DRAWER: 'FiltersDrawer',
  FARE_CALENDAR: 'FareCalendar',
  [`FARE_FAMILIES_${OUTBOUND}`]: 'FareFamiliesOutbound',
  [`FARE_FAMILIES_${INBOUND}`]: 'FareFamiliesInbound',
  [`SEARCH_RESULT_GROUP_${OUTBOUND}`]: 'SearchResultGroupOutbound',
  [`SEARCH_RESULT_GROUP_${INBOUND}`]: 'SearchResultGroupInbound',
};

export const DESKTOP_FLIGHTS_ROUTES = {
  HOME: '/{lang}/flights-home/',
  RESULTS: '/{lang}/flights/{query}',
  PAX: '/{lang}/flight/traveller/{saleId}',
  EXTRAS: '/{lang}/flight/extras/{saleId}',
  PAYMENT: '/{lang}/checkout/{saleId}',
} as const;

// TODO: combile DESKTOP_FLIGHTS_ROUTES and FLIGHTS_ROUTED and create a util function to handle replace
export const FLIGHTS_ROUTES = {
  HOME: '/mweb/flights-home',
  RESULTS: '/mweb/flights/{query}',
  PAX: '/mweb/flight/traveller/{saleId}',
  EXTRAS: '/mweb/flight/extras/{saleId}',
  PAYMENT: '/mweb/checkout/{saleId}',
} as const;

export enum AIRPORT_TYPES {
  DEPARTURE = 'departure',
  ARRIVAL = 'arrival',
}

export const AIRCRAT_TYPE_BUS = 'bus';

export const IMAGE_CDN_URL = { DEV: 'https://imgproc-dev.tjwlcdn.com', LIVE: 'https://imgproc.tjwlcdn.com' };

export const CUSTOM_FILTER_KEYS = [
  'maxStop',
  'price',
  'stop',
  'airline',
  'airport',
  'departureTime',
  'insuranceOnly',
  'hasBaggageOnly',
];
export const STOPS_FILTER_OPTIONS_KEYS = {
  ANY_NUM: 'all',
  DIRECT: '0',
  ONE_STOP: '1',
};

export const SEARCH_ACTIONS = {
  STOPS: FILTER_MAP.STOP,
  BAGGAGE: FILTER_MAP.BAGGAGE,
  AIRLINE: FILTER_MAP.AIRLINE,
  // TODO: change with sort id from sort store
  SORT: 'sort',
  FILTER: 'filters',
};

export const FILTER_DUMMY_ANY_OPTION = 'FILTER_DUMMY_ANY_OPTION';

export const AIRCRAFT_TYPES = {
  BUS: 'BUS',
  TRAIN: 'TRN',
};
export const REFRESH_MODAL_TIMEOUT = 600000;

export const JOURNEY_POINTS = {
  ORIGIN: 'origin',
  DESTINATION: 'destination',
} as const;

export const OBSERVABLE_EVENTS = {
  FLIGHT_DETAILS: 'flight-details',
  FARE_CALENDAR: 'fareCalendar',
  SEARCH_OVERLAY: 'search-overlay',
  SEARCH_OVERLAY_ORIGIN: 'search-overlay-origin',
  SEARCH_OVERLAY_DESTINATION: 'search-overlay-destination',
  SWITCH_AIRPORTS: 'switch-airports',
  UPDATE_DATES: 'update-dates',
  SEARCH_BOX_SEARCH_ERROR: 'search-box-search-error',
  RESET_EASY_APPLY: 'reset-easy-apply',
};

export const AIRLINE_SHOW_COUNT = 5;

export const PLATFORM = {
  MOBILE: 'mWeb',
  DESKTOP: 'dWeb',
} as const;

export const META_PLATFORMS = {
  WEGO: 'wego',
  SKYSCANNER: 'skyscanner',
};

export const STEP_STATUS = {
  DONE: 'done',
  ACTIVE: 'active',
  PENDING: 'pending',
} as const;

export const QUICK_FILTERS_ESTIMATION_HEIGHT = 120;

export const CONTACT_FIELDS_PREFIX = 'contact';

export const CONTACT_MEDIUM_FIELDS = ['dialCode', 'phone', 'countryId', 'email'];
export const CONTACT_NAME_FIELDS = ['title', 'firstName', 'lastName'];

export const PHONENUMBER_SEPARATOR_REGEX = /-|_|\s/g;
export const SEARCH_QUERY_SSR_TIMEOUT_S = 3;
export const GET_SALE_SSR_TIMEOUT_S = 3;

export const EXPERIRMENT_HEADERS = {
  MULTI_PNR_FARE_CALENDAR_HEADER: 'enable-multi-pnr',
  SKIP_SERVICE_FEE_HEADER: 'x-skip-service-fee',
};

export const GLOBAL = 'GLOBAL';

export const MIN_SEATS_REMAINING_THRESHOLD = 5;

export const SEATS_REMAINING_VARIANTS = {
  VARIANT_A: 'variant_a',
  VARIANT_B: 'variant_b',
} as const;

export const AIRPORT_FILTER_TYPES = {
  AIRPORT: 'airport',
  NEAR_BY: 'nearBy',
  OTHERS: 'others',
};
export const EXAMPLE_AIPORT_CODE = 'XYZ';
export const APPLE_PROVIDER = 'apple';

export const SEARCHBOX_FIELDS = {
  CABIN: 'cabin',
  PASSENGERS: 'passengers',
  DEPARTURE_DATE: 'dpartureDate',
  RETURN_DATE: 'returnDate',
  SEARCH_TYPE: 'searchType',
  DIRECT_FLIGHTS: 'directFlights',
  CHECKED_BAGGAGE: 'checkedBaggage',
} as const;

export const SAR = 'SAR';

export const FLIGHT_TYPE = {
  DOMESTIC: 'Domestic',
  INTERNATIONAL: 'International',
};
